// Scenario filter options for the Run Center journal.
//
// The backend facet returns EVERY non-deleted scenario of the workspace, and a
// workspace can legitimately hold several scenarios that localize to the same
// display name (e.g. three copies of the follow-up preset all read «Follow-up
// через 3 дня»). The dropdown therefore must:
//   1. collapse duplicate ids (defensive: the facet is keyed by id), and
//   2. DISAMBIGUATE colliding labels instead of hiding options — the filter
//      value is the scenario id, so hiding an option would make part of the
//      journal unreachable.
//
// The disambiguator is NEVER the raw scenario name: workspaces seeded from a
// preset carry the same English name on every copy, and appending it to a
// localized label produced «Follow-up через 3 дня · Follow-up after 3 days»
// three times in a row — the bug this module exists to fix. The facet carries
// no trigger type or created date either, so the only stable per-id signal is
// the id itself: its last 4 characters («Follow-up через 3 дня · #ux1l»).
// Pure and framework-free so the page tests can assert the exact option list.
import type { RunCenterScenarioFacet } from '../api/run-center.client.js';

export interface RunCenterScenarioOption {
  value: string;
  label: string;
}

/**
 * Stable, mutually unique id tails: start at the last 4 characters; if two
 * colliding options share a tail, widen it (8, 16, … full id) until every
 * tail in the group is distinct. Order-stable and deterministic.
 */
function uniqueIdTails(ids: readonly string[]): string[] {
  const taken = new Set<string>();
  return ids.map((id) => {
    let length = Math.min(4, id.length);
    let tail = id.slice(-length);
    while (taken.has(tail) && length < id.length) {
      length = Math.min(length * 2, id.length);
      tail = id.slice(-length);
    }
    taken.add(tail);
    return tail;
  });
}

/**
 * Builds one option per unique scenario id. The base label is always the
 * localized display name; when two ids resolve to the same label, a short
 * stable id tail is appended (`«{label} · #{tail}»`) so every option stays
 * selectable and distinguishable.
 */
export function buildScenarioFilterOptions(
  scenarios: readonly RunCenterScenarioFacet[],
  displayName: (scenario: RunCenterScenarioFacet) => string,
): RunCenterScenarioOption[] {
  const seen = new Set<string>();
  const unique: RunCenterScenarioFacet[] = [];
  for (const scenario of scenarios) {
    if (seen.has(scenario.id)) continue;
    seen.add(scenario.id);
    unique.push(scenario);
  }

  const labels = unique.map(displayName);
  const labelCounts = new Map<string, number>();
  for (const label of labels) {
    labelCounts.set(label, (labelCounts.get(label) ?? 0) + 1);
  }

  // Group colliding options by label; only those get a suffix.
  const collidingGroups = new Map<string, number[]>();
  labels.forEach((label, index) => {
    if ((labelCounts.get(label) ?? 0) <= 1) return;
    const group = collidingGroups.get(label);
    if (group !== undefined) group.push(index);
    else collidingGroups.set(label, [index]);
  });

  const suffixes = new Map<number, string>();
  for (const [, indices] of collidingGroups) {
    const tails = uniqueIdTails(indices.map((index) => unique[index]?.id ?? ''));
    indices.forEach((index, position) => suffixes.set(index, tails[position] ?? ''));
  }

  return unique.map((scenario, index) => {
    const label = labels[index] ?? scenario.id;
    const suffix = suffixes.get(index);
    return { value: scenario.id, label: suffix === undefined ? label : `${label} · #${suffix}` };
  });
}
